/**
 * Liste der Betriebstage (Spec 7).
 *
 * Gegliedert nach Abrechnungszeitraum, nicht nach Kalendermonat: abgerechnet
 * wird halbmonatlich, und ein Monatsblatt führt immer zwei Auszahlungen
 * zusammen. Standard ist der Zeitraum, an dem gerade gearbeitet wird.
 */
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { api, type BusinessDaySummary, type WorkflowDto } from "../lib/api";
import { ART_KURZ, ART_LABEL, beschreibung } from "../lib/berechnung";
import { euro, longDate } from "../lib/format";
import { fehlerKurz, type CalcErrorDto } from "../lib/calcErrors";
import { ResetPeriod } from "../components/ResetPeriod";
import {
  Amount,
  Empty,
  ErrorBox,
  Info,
  PageHeader,
  Spinner,
  StatusChip,
} from "../components/ui";

interface Sammelergebnis {
  freigegeben: string[];
  liegengeblieben: { businessDate: string; grund: string }[];
}

export function BusinessDays() {
  const [gewaehlteKey, setGewaehlteKey] = useState<string | null>(null);
  const [ergebnis, setErgebnis] = useState<Sammelergebnis | null>(null);
  const [zuruecksetzenOffen, setZuruecksetzenOffen] = useState(false);
  const [tagAnlegenOffen, setTagAnlegenOffen] = useState(false);
  const [sicherung, setSicherung] = useState<string | null>(null);
  const [neuerTag, setNeuerTag] = useState("");
  const [neuerTitel, setNeuerTitel] = useState("");
  const queryClient = useQueryClient();

  const workflow = useQuery({
    queryKey: ["workflow"],
    queryFn: () => api.get<WorkflowDto>("/workflow"),
  });
  const days = useQuery({
    queryKey: ["business-days"],
    queryFn: () => api.get<{ days: BusinessDaySummary[] }>("/business-days"),
  });

  // Ein Betriebstag entsteht sonst erst durch einen Import. War nur der
  // Wintergarten offen, dessen Geräte in keinem Export stehen, gäbe es den Tag
  // nirgends — und es liesse sich auch nichts dazu erfassen.
  const tagAnlegen = useMutation({
    mutationFn: () =>
      api.post("/business-days", {
        businessDate: neuerTag,
        ...(neuerTitel.trim() ? { eventTitle: neuerTitel.trim() } : {}),
      }),
    onSuccess: async () => {
      setTagAnlegenOffen(false);
      setNeuerTag("");
      setNeuerTitel("");
      await queryClient.invalidateQueries();
    },
  });

  const rechnen = useMutation({
    mutationFn: (bereich: { from: string; to: string }) =>
      api.post<{ berechnet: string[]; liegengeblieben: { businessDate: string; grund: string }[] }>(
        "/business-days/calculate-ready",
        bereich,
      ),
    onSuccess: async (data) => {
      setErgebnis({ freigegeben: data.berechnet, liegengeblieben: data.liegengeblieben });
      await queryClient.invalidateQueries();
    },
  });

  const freigeben = useMutation({
    mutationFn: (bereich: { from: string; to: string }) =>
      api.post<Sammelergebnis>("/business-days/approve", bereich),
    onSuccess: async (data) => {
      setErgebnis(data);
      await queryClient.invalidateQueries();
    },
  });

  if (days.isLoading || workflow.isLoading) return <Spinner label="Betriebstage werden geladen" />;
  if (days.error) return <ErrorBox error={days.error} />;

  const alle = days.data?.days ?? [];
  const perioden = workflow.data?.perioden ?? [];
  const periode =
    perioden.find((p) => p.key === gewaehlteKey) ?? workflow.data?.periode ?? perioden[0] ?? null;
  const sichtbar = periode
    ? alle.filter((d) => d.businessDate >= periode.from && d.businessDate <= periode.to)
    : alle;

  const index = perioden.findIndex((p) => p.key === periode?.key);
  const berechnet = sichtbar.filter((d) => d.status === "CALCULATED");
  const entwurf = sichtbar.filter((d) => d.status === "DRAFT");
  const bereich = periode ? { from: periode.from, to: periode.to } : null;

  return (
    <>
      <PageHeader
        label="Abrechnungszeitraum"
        title={periode?.label ?? "Alle Tage"}
        actions={
          /*
           * Ausnahmefunktionen. Neben der täglichen Arbeit stehend lenken sie
           * ab und laden zur Fehlbedienung ein — „Zeitraum zurücksetzen" direkt
           * neben „Tage freigeben" ist ein schlechter Nachbar.
           */
          periode && !zuruecksetzenOffen ? (
            <details className="relative">
              <summary className="btn-ghost cursor-pointer select-none list-none">
                Weitere Optionen
              </summary>
              <div className="absolute right-0 z-20 mt-1 w-64 rounded border border-line bg-page p-2 shadow-lg">
                <button
                  className="btn-ghost w-full justify-start"
                  onClick={() => setTagAnlegenOffen((v) => !v)}
                >
                  Betriebstag anlegen
                </button>
                <button
                  className="btn-ghost w-full justify-start text-alert"
                  onClick={() => setZuruecksetzenOffen(true)}
                >
                  Zeitraum zurücksetzen
                </button>
              </div>
            </details>
          ) : null
        }
      >
        Ein Betriebstag läuft vom Kalendertag bis 10:00 Uhr des Folgetags. Abgerechnet wird
        halbmonatlich; dieser Zeitraum ergibt eine Auszahlung.
      </PageHeader>

      {tagAnlegenOffen ? (
        <div className="card mb-6 p-5">
          <h3 className="mb-1 text-lg">Betriebstag anlegen</h3>
          <p className="mb-4 max-w-2xl text-sm text-muted">
            Für einen Abend, zu dem kein Import etwas liefert, etwa wenn nur der Wintergarten
            offen hatte. Danach lassen sich am Tag Kassen und Stunden von Hand erfassen.
          </p>
          <div className="mb-4 grid gap-3 sm:grid-cols-2">
            <label className="block">
              <span className="label mb-1 block">Betriebstag</span>
              <input
                type="date"
                className="field"
                value={neuerTag}
                onChange={(e) => setNeuerTag(e.target.value)}
              />
            </label>
            <label className="block">
              <span className="label mb-1 block">Veranstaltung (freiwillig)</span>
              <input
                className="field"
                value={neuerTitel}
                onChange={(e) => setNeuerTitel(e.target.value)}
                placeholder="z. B. Nur Wintergarten offen"
              />
            </label>
          </div>
          {tagAnlegen.error ? <ErrorBox error={tagAnlegen.error} /> : null}
          <div className="flex gap-2">
            <button
              className="btn-recommend"
              disabled={neuerTag === "" || tagAnlegen.isPending}
              onClick={() => tagAnlegen.mutate()}
            >
              {tagAnlegen.isPending ? "Legt an" : "Anlegen"}
            </button>
            <button className="btn-ghost" onClick={() => setTagAnlegenOffen(false)}>
              Abbrechen
            </button>
          </div>
        </div>
      ) : null}

      {periode && zuruecksetzenOffen ? (
        <div className="mb-6">
          <ResetPeriod
            periode={periode}
            onDone={(sicherung) => {
              setZuruecksetzenOffen(false);
              setGewaehlteKey(null);
              setErgebnis(null);
              setSicherung(sicherung);
            }}
            onCancel={() => setZuruecksetzenOffen(false)}
          />
        </div>
      ) : null}

      {perioden.length > 1 && periode ? (
        <div className="mb-4 flex flex-wrap items-center gap-2">
          {/* Jeder Monat bringt zwei Zeiträume; als Knopfleiste wären das nach
              einem Jahr vierundzwanzig. Blättern für den Alltag, Auswahlfeld
              für den Sprung zurück. */}
          <button
            className="btn-ghost"
            disabled={index >= perioden.length - 1}
            onClick={() => {
              setGewaehlteKey(perioden[index + 1]?.key ?? null);
              setErgebnis(null);
            }}
          >
            ← früher
          </button>
          <select
            className="field w-auto"
            value={periode.key}
            onChange={(e) => {
              setGewaehlteKey(e.target.value);
              setErgebnis(null);
            }}
          >
            {perioden.map((p) => (
              <option key={p.key} value={p.key}>
                {p.label}
                {p.istAltbestand ? " · Altbestand" : p.offeneTage > 0 ? ` · ${p.offeneTage} offen` : ""}
              </option>
            ))}
          </select>
          <button
            className="btn-ghost"
            disabled={index <= 0}
            onClick={() => {
              setGewaehlteKey(perioden[index - 1]?.key ?? null);
              setErgebnis(null);
            }}
          >
            später →
          </button>
        </div>
      ) : null}

      {periode?.istAltbestand ? (
        <p className="mb-4 rounded border border-line bg-surface px-4 py-3 text-sm text-muted">
          <strong className="font-medium text-ink">Altbestand.</strong> Diese Tage stammen aus
          der alten Excel-Datei und sind gesperrt. Übernommen wurden nur Person, Tag und
          Betrag; Kassenzahlen und Stunden gibt es dazu nicht. Eine Neuberechnung ist
          ausgeschlossen, damit der ausgezahlte Bestand unverändert bleibt.
        </p>
      ) : bereich && (berechnet.length > 0 || entwurf.length > 0) ? (
        <div className="mb-4 rounded border border-accent/30 bg-accent/5 px-4 py-3 text-sm">
          {berechnet.length > 0 ? (
            <>
              <strong className="text-ink">
                {berechnet.length === 1
                  ? "1 Tag ist gerechnet und wartet auf Freigabe."
                  : `${berechnet.length} Tage sind gerechnet und warten auf Freigabe.`}
              </strong>{" "}
              Die Prüfung ist im Kassen-Review passiert; die Freigabe ist eine Entscheidung,
              nicht {berechnet.length} Entscheidungen.
              <div className="mt-2">
                <button
                  className="btn-recommend"
                  onClick={() => freigeben.mutate(bereich)}
                  disabled={freigeben.isPending}
                >
                  {freigeben.isPending
                    ? "Gibt frei"
                    : berechnet.length === 1
                      ? "Tag freigeben"
                      : `Alle ${berechnet.length} Tage freigeben`}
                </button>
              </div>
            </>
          ) : (
            <>
              <strong className="text-ink">
                {entwurf.length === 1
                  ? "1 Tag ist noch nicht gerechnet."
                  : `${entwurf.length} Tage sind noch nicht gerechnet.`}
              </strong>{" "}
              Normalerweise passiert das von selbst. Bleibt ein Tag liegen, nennt der Knopf den
              Grund.
              <div className="mt-2">
                <button
                  className="btn-outline"
                  onClick={() => rechnen.mutate(bereich)}
                  disabled={rechnen.isPending}
                >
                  {rechnen.isPending ? "Rechnet" : "Rechenbare Tage berechnen"}
                </button>
              </div>
            </>
          )}
        </div>
      ) : null}

      {sicherung ? (
        <p className="mb-4 rounded border border-ok/30 bg-ok/5 px-4 py-3 text-sm">
          Zurückgesetzt. Die Sicherung davor liegt unter{" "}
          <span className="font-mono text-xs">{sicherung}</span> und lässt sich mit{" "}
          <span className="font-mono text-xs">pg_restore</span> zurückspielen.
        </p>
      ) : null}

      {ergebnis ? (
        <div className="mb-4 rounded border border-line bg-surface px-4 py-3 text-sm">
          {ergebnis.freigegeben.length > 0 ? (
            <p className="text-ok">{ergebnis.freigegeben.length} Tage erledigt.</p>
          ) : null}
          {ergebnis.liegengeblieben.length > 0 ? (
            <>
              <p className="mb-1 text-muted">Liegen geblieben:</p>
              <ul className="space-y-1 text-xs text-muted">
                {ergebnis.liegengeblieben.map((l) => (
                  <li key={l.businessDate}>
                    <Link to={`/betriebstage/${l.businessDate}`} className="hover:text-accent">
                      {longDate(l.businessDate)}
                    </Link>{" "}
                    — {l.grund}
                  </li>
                ))}
              </ul>
            </>
          ) : null}
        </div>
      ) : null}

      {freigeben.error ? <ErrorBox error={freigeben.error} /> : null}
      {rechnen.error ? <ErrorBox error={rechnen.error} /> : null}

      {sichtbar.length === 0 ? (
        <Empty>Keine Betriebstage in diesem Zeitraum. Zuerst Kassendaten importieren.</Empty>
      ) : (
        <div className="card overflow-hidden">
          <table className="tbl">
            <thead>
              <tr>
                <th className="w-36">Tag</th>
                <th>Veranstaltung</th>
                <th className="w-28 text-right">
                  Trinkgeld
                  <Info text="Summe aller Töpfe des Tages. Ein Strich heißt: noch nicht berechnet." />
                </th>
                <th className="min-w-[14rem]">Was noch fehlt</th>
                <th className="w-28">Status</th>
              </tr>
            </thead>
            <tbody>
              {sichtbar.map((d) => (
                <tr key={d.businessDate} className={d.isBalanced === false ? "bg-alert/5" : ""}>
                  <td>
                    <Link to={`/betriebstage/${d.businessDate}`} className="hover:text-accent">
                      {longDate(d.businessDate)}
                    </Link>
                  </td>
                  <td className="max-w-lg truncate text-muted" title={d.eventTitle ?? ""}>
                    {/* Nur, wo noch entschieden wird: nach einer neuen Vorgabe stünde es
                        sonst an jedem ausgezahlten Tag. */}
                    {d.berechnung && !d.berechnung.istVorgabe && d.status !== "LOCKED" ? (
                      <span
                        className="chip-warn mr-2"
                        title={`Nicht nach der Vorgabe gerechnet: ${ART_LABEL[d.berechnung.art]}, ${beschreibung(d.berechnung.gruppen)}`}
                      >
                        {ART_KURZ[d.berechnung.art]}
                      </span>
                    ) : null}
                    {d.eventTitle ?? "–"}
                  </td>
                  <td>
                    <Amount value={euro(d.tipTotal)} muted={d.tipTotal === null} />
                  </td>
                  <td>
                    {d.openBlockingFlags.length > 0 ? (
                      <Link
                        to="/kassen-review"
                        className="chip-alert hover:border-alert"
                        title="Zum Kassen-Review"
                      >
                        {d.openBlockingFlags.length} zu klären
                      </Link>
                    ) : (d.calcErrors ?? []).length > 0 ? (
                      <Link
                        to={`/betriebstage/${d.businessDate}`}
                        className="text-xs text-warn hover:underline"
                        title="Zum Betriebstag: dort steht, was zu tun ist"
                      >
                        {fehlerKurz(d.calcErrors?.[0] as CalcErrorDto)}
                      </Link>
                    ) : (d.calcWarnings ?? []).length > 0 ? (
                      // Hinweis, kein Hindernis: der Tag lässt sich freigeben.
                      <Link
                        to={`/betriebstage/${d.businessDate}`}
                        className="text-xs text-muted hover:underline"
                        title={fehlerKurz(d.calcWarnings?.[0] as CalcErrorDto)}
                      >
                        Hinweis zum Tagesausgleich
                      </Link>
                    ) : (
                      <span className="text-xs text-muted">–</span>
                    )}
                  </td>
                  <td>
                    <StatusChip status={d.status} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}
